import { type Emblem as EmblemData, type EmblemColor, type EmblemIcon, type EmblemShape } from '@sfco/shared';

/**
 * Pure-SVG emblem renderer. Accepts a structured `Emblem` and draws a
 * deterministic crest at any size. No CSS-in-JS — hex values are baked so
 * the emblem reads the same inside and outside React.
 *
 * Use cases: command bar, profile page, leaderboards, signup designer.
 */
export interface EmblemProps {
  data: EmblemData;
  size?: number;
  /** Optional className applied to the root <svg>. */
  className?: string;
}

export function Emblem({ data, size = 48, className }: EmblemProps) {
  const primary = COLOR_HEX[data.primary];
  const accent = COLOR_HEX[data.accent];
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      aria-label={`Emblem: ${data.shape} / ${data.icon}`}
    >
      {renderShape(data.shape, primary, accent)}
      <g transform="translate(50,50) scale(0.42) translate(-50,-50)">
        {renderIcon(data.icon, accent)}
      </g>
    </svg>
  );
}

// ── Palette lookup ─────────────────────────────────────────────────────────
// Matches the Cassette Charter palette in tailwind.config.ts. Keep in sync.
const COLOR_HEX: Record<EmblemColor, string> = {
  brass: '#8A6D2F',
  'crt-amber': '#F5A623',
  'crt-red': '#E55A4F',
  'crt-blue': '#6FB8D4',
  'crt-green': '#8FE36B',
  ink: '#E8D8A8',
  paperDeep: '#12140E',
};

// ── Shapes ─────────────────────────────────────────────────────────────────

function renderShape(shape: EmblemShape, primary: string, accent: string) {
  const stroke = accent;
  switch (shape) {
    case 'hex':
      return (
        <polygon
          points="50,4 92,26 92,74 50,96 8,74 8,26"
          fill={primary}
          stroke={stroke}
          strokeWidth="3"
        />
      );
    case 'shield':
      return (
        <path
          d="M 50 4 L 94 18 L 94 50 Q 94 80 50 96 Q 6 80 6 50 L 6 18 Z"
          fill={primary}
          stroke={stroke}
          strokeWidth="3"
        />
      );
    case 'roundel':
      return (
        <g>
          <circle cx="50" cy="50" r="46" fill={primary} stroke={stroke} strokeWidth="3" />
          <circle
            cx="50"
            cy="50"
            r="36"
            fill="none"
            stroke={stroke}
            strokeOpacity="0.35"
            strokeWidth="1"
          />
        </g>
      );
    case 'diamond':
      return (
        <polygon
          points="50,4 96,50 50,96 4,50"
          fill={primary}
          stroke={stroke}
          strokeWidth="3"
        />
      );
    case 'chevron':
      return (
        <path
          d="M 6 20 L 50 4 L 94 20 L 94 60 L 50 96 L 6 60 Z"
          fill={primary}
          stroke={stroke}
          strokeWidth="3"
        />
      );
    case 'square-notched':
      return (
        <path
          d="M 14 4 H 86 L 96 14 V 86 L 86 96 H 14 L 4 86 V 14 Z"
          fill={primary}
          stroke={stroke}
          strokeWidth="3"
        />
      );
  }
}

// ── Icons ──────────────────────────────────────────────────────────────────
// Each renders into a 0..100 viewBox (scaled by the Emblem container).
// Strokes are `accent`; fills stay transparent so the primary shape shows.

function renderIcon(icon: EmblemIcon, accent: string) {
  const s = { stroke: accent, strokeWidth: 4, fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
  switch (icon) {
    case 'drone':
      return (
        <g {...s}>
          <circle cx="50" cy="50" r="12" />
          <line x1="20" y1="20" x2="36" y2="36" />
          <line x1="80" y1="20" x2="64" y2="36" />
          <line x1="20" y1="80" x2="36" y2="64" />
          <line x1="80" y1="80" x2="64" y2="64" />
          <circle cx="20" cy="20" r="8" />
          <circle cx="80" cy="20" r="8" />
          <circle cx="20" cy="80" r="8" />
          <circle cx="80" cy="80" r="8" />
        </g>
      );
    case 'gear':
      return (
        <g {...s}>
          <circle cx="50" cy="50" r="18" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
            <line
              key={deg}
              x1={50 + 26 * Math.cos((deg * Math.PI) / 180)}
              y1={50 + 26 * Math.sin((deg * Math.PI) / 180)}
              x2={50 + 40 * Math.cos((deg * Math.PI) / 180)}
              y2={50 + 40 * Math.sin((deg * Math.PI) / 180)}
            />
          ))}
          <circle cx="50" cy="50" r="6" />
        </g>
      );
    case 'anvil':
      return (
        <g {...s}>
          <path d="M 20 40 H 80 L 70 55 H 30 Z" />
          <rect x="42" y="55" width="16" height="10" />
          <rect x="30" y="65" width="40" height="8" />
        </g>
      );
    case 'rocket':
      return (
        <g {...s}>
          <path d="M 50 15 Q 65 35 65 60 V 75 H 35 V 60 Q 35 35 50 15 Z" />
          <circle cx="50" cy="45" r="6" />
          <path d="M 35 60 L 25 80 L 40 75 Z" />
          <path d="M 65 60 L 75 80 L 60 75 Z" />
        </g>
      );
    case 'crucible':
      return (
        <g {...s}>
          <path d="M 25 35 H 75 L 68 80 Q 50 85 32 80 Z" />
          <line x1="20" y1="35" x2="80" y2="35" />
        </g>
      );
    case 'star':
      return (
        <g {...s}>
          <polygon points="50,15 59,40 85,40 64,56 72,82 50,67 28,82 36,56 15,40 41,40" />
        </g>
      );
    case 'hex-grid':
      return (
        <g {...s}>
          <polygon points="50,22 66,32 66,52 50,62 34,52 34,32" />
          <polygon points="30,52 46,62 46,82 30,92 14,82 14,62" transform="translate(0,-10)" />
          <polygon points="70,52 86,62 86,82 70,92 54,82 54,62" transform="translate(0,-10)" />
        </g>
      );
    case 'lightning':
      return (
        <g {...s}>
          <polygon points="55,15 30,55 48,55 40,85 72,42 54,42 62,15" />
        </g>
      );
    case 'leaf':
      return (
        <g {...s}>
          <path d="M 20 70 Q 20 20 75 25 Q 80 70 25 80 Z" />
          <line x1="30" y1="70" x2="65" y2="40" />
        </g>
      );
    case 'crown':
      return (
        <g {...s}>
          <path d="M 20 70 L 25 35 L 40 55 L 50 25 L 60 55 L 75 35 L 80 70 Z" />
          <line x1="20" y1="78" x2="80" y2="78" />
        </g>
      );
    case 'compass':
      return (
        <g {...s}>
          <circle cx="50" cy="50" r="32" />
          <polygon points="50,20 58,50 50,80 42,50" />
        </g>
      );
    case 'flask':
      return (
        <g {...s}>
          <path d="M 40 20 H 60 V 40 L 75 80 H 25 L 40 40 Z" />
          <line x1="32" y1="62" x2="68" y2="62" />
        </g>
      );
    case 'wrench':
      return (
        <g {...s}>
          <path d="M 70 20 A 15 15 0 1 0 55 45 L 25 75 L 30 82 L 60 52 A 15 15 0 0 0 70 20 Z" />
        </g>
      );
    case 'beacon':
      return (
        <g {...s}>
          <circle cx="50" cy="30" r="10" />
          <line x1="50" y1="40" x2="50" y2="80" />
          <line x1="30" y1="80" x2="70" y2="80" />
          <line x1="35" y1="20" x2="25" y2="15" />
          <line x1="65" y1="20" x2="75" y2="15" />
        </g>
      );
    case 'shield-cross':
      return (
        <g {...s}>
          <path d="M 50 15 L 80 25 L 80 50 Q 80 75 50 85 Q 20 75 20 50 L 20 25 Z" />
          <line x1="50" y1="30" x2="50" y2="70" />
          <line x1="30" y1="50" x2="70" y2="50" />
        </g>
      );
    case 'satellite':
      return (
        <g {...s}>
          <rect x="40" y="40" width="20" height="20" />
          <line x1="40" y1="50" x2="20" y2="30" />
          <line x1="40" y1="50" x2="20" y2="70" />
          <line x1="60" y1="50" x2="80" y2="30" />
          <line x1="60" y1="50" x2="80" y2="70" />
        </g>
      );
    case 'cog-ring':
      return (
        <g {...s}>
          <circle cx="50" cy="50" r="28" />
          <circle cx="50" cy="50" r="12" />
        </g>
      );
    case 'triangle':
      return (
        <g {...s}>
          <polygon points="50,20 82,80 18,80" />
        </g>
      );
    case 'circle-dot':
      return (
        <g {...s}>
          <circle cx="50" cy="50" r="28" />
          <circle cx="50" cy="50" r="6" fill={accent} />
        </g>
      );
    case 'waves':
      return (
        <g {...s}>
          <path d="M 15 50 Q 30 35 45 50 T 75 50" />
          <path d="M 15 65 Q 30 50 45 65 T 75 65" />
        </g>
      );
  }
}
